import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  withRouter,
  Route,
  Link,
} from 'react-router-dom';
import {
  Breadcrumb,
  Row,
  Col,
  notification,
} from 'antd';
import _ from 'lodash';
import moment from 'moment-timezone';
import 'assets/style/app/index.css';
import * as SRC from 'modules/styled';
import * as TXT from 'modules/styled/text';
import NavigationBar from 'modules/navigationBar';
import * as C from 'actions/constants';
import * as REPORTS_ACTIONS from 'actions/reports';
import * as SESSION_ACTIONS from 'actions/session';
import AuthHOC from 'containers/AuthHOC';
import IdleTimer from 'react-idle-timer';
import { isAdminUser } from 'utils/currentUser';

const INACTIVITY_TIME_IN_MINUTES = 60;

const initialBreadcrumb = [
  {
    label: 'Home',
    endpoint: '/reports',
  },
];

class RouteLayout extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      breadcrumbItems: initialBreadcrumb,
      title: props.title,
      rightContent: null,
    };

    this.setBreadcrumb = this.setBreadcrumb.bind(this);
    this.setTitle = this.setTitle.bind(this);
    this.setRightContent = this.setRightContent.bind(this);
    this.handleSignOut = this.handleSignOut.bind(this);
    this.onIdle = this.onIdle.bind(this);
  }

  componentDidMount() {
    const { report, setReport } = this.props;
    const localStorageReport = localStorage.getItem('selectedReport');
    const sameReport = _.isEqual(report, localStorageReport);

    if (!_.isEmpty(localStorageReport) && !sameReport) {
      setReport(JSON.parse(localStorageReport));
    }
  }

  componentDidUpdate() {
    const { title: titleFromProps } = this.props;
    const { title: titleFromState } = this.state;
    this.setTitle(titleFromProps || titleFromState);
  }

  setBreadcrumb(breadcrumbItems = []) {
    this.setState({ breadcrumbItems: [...initialBreadcrumb, ...breadcrumbItems] });
  }

  setTitle(newTitle) {
    const { title } = this.state;
    if (title !== newTitle) {
      this.setState({ title: newTitle });
    }
  }

  setRightContent(rightContent) {
    this.setState({ rightContent });
  }

  handleSignOut() {
    const { signOut, auth, history } = this.props;
    const token = _.get(auth, 'access_token');

    signOut(token)
      .then((res) => {
        if (res.type === C.SIGN_OUT_SUCCESS) {
          history.push('/');
        }
      });
  }

  onIdle() {
    notification.error({ message: "You've been logged out", description: "We've noticed that you aren't active in the session, so we logged you out for your safety. Please log in again.", duration: 0 });
    this.handleSignOut();
  }

  render() {
    const { component: Component, signOut, ...props } = this.props;
    const {
      breadcrumbItems, title, rightContent,
    } = this.state;

    // Check if current route is ReportsDetail
    const isReportsDetail = props.path === '/reports/:id';

    return (
      <Route
        {...props}
        render={(matchProps) => (
          <SRC.MainScreen>
            <NavigationBar handleSignOut={this.handleSignOut} />
            <SRC.PageHeader sticky={isReportsDetail}>
              <SRC.PageHeaderContent>
                <SRC.PageHeaderLeft>
                  {breadcrumbItems.length > 0 && (
                    <Breadcrumb>
                      {breadcrumbItems.map((item) => (
                        <Breadcrumb.Item key={item.label}>
                          <Link to={item.endpoint}>{item.label}</Link>
                        </Breadcrumb.Item>
                      ))}
                    </Breadcrumb>
                  )}
                  <TXT.Title style={{ 
                    margin: 0, 
                    fontSize: '20px', 
                    lineHeight: '18px' 
                  }}>
                    {title}
                  </TXT.Title>
                </SRC.PageHeaderLeft>
                {rightContent && (
                  <div>
                    {rightContent}
                  </div>
                )}
              </SRC.PageHeaderContent>
            </SRC.PageHeader>
            <SRC.MainContent>
              <SRC.AppContent style={{ 
                paddingTop: isReportsDetail ? '74px' : '14px',
                paddingBottom: '24px'
              }}>
                <Component
                  {...matchProps}
                  setBreadcrumb={this.setBreadcrumb}
                  setTitle={this.setTitle}
                  setRightContent={this.setRightContent}
                />
              </SRC.AppContent>
            </SRC.MainContent>
            <SRC.Footer>
              <SRC.AppContent>
                <Row>
                  <Col xs={12}>
                    <TXT.Base txtcolor="lightgray">v1.37.0 | Timezone: {moment.tz.guess()}</TXT.Base>
                  </Col>
                  <Col xs={12}>
                    <TXT.Base align="right" txtcolor="lightgray">
                      © {moment().format('YYYY')} <a href="https://www.pospulse.com/" rel="noopener noreferrer" target="_blank">POSpulse GmbH</a>
                    </TXT.Base>
                  </Col>
                </Row>
              </SRC.AppContent>
            </SRC.Footer>
            { !isAdminUser() && (
              <IdleTimer timeout={INACTIVITY_TIME_IN_MINUTES * 1000 * 60} onIdle={this.onIdle} />
            )}
          </SRC.MainScreen>
        )}
      />
    );
  }
}

RouteLayout.defaultProps = {
  title: '',
};

RouteLayout.propTypes = {
  title: PropTypes.string,
  report: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // eslint-disable-line
  component: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired, // eslint-disable-line
  auth: PropTypes.object.isRequired, // eslint-disable-line
  setReport: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  report: state.reports.report,
});

const mapDispatchToProps = {
  setReport: REPORTS_ACTIONS.setReport,
  signOut: SESSION_ACTIONS.signOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AuthHOC(RouteLayout)));
